/**
 * # AsFormField Component
 *
 * A light-DOM, **form-associated** custom element that wraps a native text input
 * while behaving like a first-class form control. Unlike `as-string-input` (which
 * wraps an *unnamed* native input and is geared toward Paramate), `as-form-field`
 * participates in HTML forms directly via the `ElementInternals` API:
 *
 * - The HOST element is the form control. Its own `name` attribute is what gets
 *   submitted, so `new FormData(form)` and native form submission see the value
 *   with zero wrapped-input naming hacks.
 * - Validity is mirrored from the inner native input onto the host, so
 *   `:valid`/`:invalid`, `checkValidity()` and constraint validation all work.
 * - `<label for="hostId">` works because form-associated elements are labelable;
 *   clicking the label focuses the inner input.
 *
 * Because the inner input keeps its own value and we never re-assign `.value`
 * during typing, there is no caret-reset ("cursor jumps to the front") on Android.
 *
 * ## Browser support
 * Form-associated custom elements are supported in Chromium (Chrome/Edge/Android
 * WebView) since Chrome 77, Firefox 98, and Safari 16.4+.
 *
 * ## Attributes
 * - `value`            initial / reflected value
 * - `name`             form field name (used by FormData / submission)
 * - `type`             input type (text, email, password, tel, ...). Default: text
 * - `placeholder`
 * - `required`, `disabled`, `readonly`
 * - `autocomplete`, `inputmode`, `enterkeyhint`, `autocapitalize`, `spellcheck`
 * - `pattern`, `minlength`, `maxlength`
 * - `label-default-text`, `label-dict-key`  optional built-in label (via as-text)
 *
 * ## Events
 * The inner native input lives in the light DOM, so its native `input` and
 * `change` events bubble through the host as usual. Consumers can listen on the
 * form/host directly. `.value` reads/writes the current value.
 */

// Attributes that are passed straight through to the inner <input>.
const INPUT_PASSTHROUGH_ATTRS = [
  'type',
  'placeholder',
  'autocomplete',
  'inputmode',
  'enterkeyhint',
  'autocapitalize',
  'spellcheck',
  'pattern',
  'minlength',
  'maxlength',
] as const;

const STYLE_ID = 'as-form-field-styles';
// Intentionally minimal: the host is a block, but the inner <input> is left
// UNSTYLED so it inherits the consuming app's input styling (e.g. the auth
// form's `.auth-form-group input` rules) exactly. Injecting font/width/box-sizing
// here would override those app rules (same specificity, later in source) and
// make the field look different from native inputs.
const STYLE_TEXT = /* css */ `
  as-form-field { display: block; }
  as-form-field .as-form-field__label { display: none; align-items: center; gap: 4px; }
`;

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const styleEl = document.createElement('style');
  styleEl.id = STYLE_ID;
  styleEl.textContent = STYLE_TEXT;
  document.head.appendChild(styleEl);
}

/** Convert a live ValidityState into the plain flags object setValidity() expects. */
function validityToFlags(v: ValidityState): ValidityStateFlags {
  return {
    valueMissing: v.valueMissing,
    typeMismatch: v.typeMismatch,
    patternMismatch: v.patternMismatch,
    tooLong: v.tooLong,
    tooShort: v.tooShort,
    rangeUnderflow: v.rangeUnderflow,
    rangeOverflow: v.rangeOverflow,
    stepMismatch: v.stepMismatch,
    badInput: v.badInput,
    customError: v.customError,
  };
}

export class AsFormField extends HTMLElement {
  // Opt into form participation.
  static formAssociated = true;

  static get observedAttributes(): string[] {
    return [
      ...INPUT_PASSTHROUGH_ATTRS,
      'value',
      'required',
      'disabled',
      'readonly',
      'label-default-text',
      'label-dict-key',
    ];
  }

  private _internals: ElementInternals;
  private _inputEl: HTMLInputElement | null = null;
  private _labelRowEl: HTMLElement | null = null;
  private _labelTextEl: HTMLElement | null = null;
  private _hasRendered = false;
  // Backing value used before the input is rendered.
  private _value = '';
  // Value captured at form-reset time (the attribute value at construction).
  private _initialValue = '';

  constructor() {
    super();
    this._internals = this.attachInternals();
    this._handleInput = this._handleInput.bind(this);
    this._handleChange = this._handleChange.bind(this);
  }

  // ============================================================================
  // Lifecycle
  // ============================================================================

  connectedCallback(): void {
    if (!this._hasRendered) {
      this._render();
      this._hasRendered = true;
      this._initialValue = this.getAttribute('value') ?? '';
    }

    // Apply current attributes to the freshly-rendered input.
    for (const attr of INPUT_PASSTHROUGH_ATTRS) {
      this._reflectPassthrough(attr, this.getAttribute(attr));
    }
    this._applyValue(this.getAttribute('value') ?? this._value);
    this._applyBooleanState();

    this._inputEl?.addEventListener('input', this._handleInput);
    this._inputEl?.addEventListener('change', this._handleChange);

    this._updateLabel();
    this._syncFormValue();
    this._syncValidity();
  }

  disconnectedCallback(): void {
    this._inputEl?.removeEventListener('input', this._handleInput);
    this._inputEl?.removeEventListener('change', this._handleChange);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;

    if ((INPUT_PASSTHROUGH_ATTRS as readonly string[]).includes(name)) {
      this._reflectPassthrough(name, newValue);
      this._syncValidity();
      return;
    }

    switch (name) {
      case 'value':
        // Programmatic / attribute-driven value change (not user typing).
        this._applyValue(newValue ?? '');
        this._syncFormValue();
        this._syncValidity();
        break;
      case 'required':
      case 'disabled':
      case 'readonly':
        this._applyBooleanState();
        this._syncValidity();
        break;
      case 'label-default-text':
      case 'label-dict-key':
        this._updateLabel();
        break;
    }
  }

  // ============================================================================
  // Form-associated lifecycle callbacks
  // ============================================================================

  formDisabledCallback(disabled: boolean): void {
    if (this._inputEl) this._inputEl.disabled = disabled;
  }

  formResetCallback(): void {
    this._applyValue(this._initialValue);
    this._syncFormValue();
    this._syncValidity();
  }

  formStateRestoreCallback(state: string | File | FormData | null): void {
    if (typeof state === 'string') {
      this._applyValue(state);
      this._syncFormValue();
      this._syncValidity();
    }
  }

  // ============================================================================
  // Public form-control API (delegates to ElementInternals / inner input)
  // ============================================================================

  get value(): string {
    return this._inputEl ? this._inputEl.value : this._value;
  }

  set value(val: string) {
    this._applyValue(val ?? '');
    this._syncFormValue();
    this._syncValidity();
  }

  get form(): HTMLFormElement | null {
    return this._internals.form;
  }

  get name(): string {
    return this.getAttribute('name') ?? '';
  }

  get validity(): ValidityState {
    return this._internals.validity;
  }

  get validationMessage(): string {
    return this._internals.validationMessage;
  }

  get willValidate(): boolean {
    return this._internals.willValidate;
  }

  checkValidity(): boolean {
    return this._internals.checkValidity();
  }

  reportValidity(): boolean {
    return this._internals.reportValidity();
  }

  /** Delegate focus to the inner input (so <label for> and .focus() behave natively). */
  override focus(options?: FocusOptions): void {
    this._inputEl?.focus(options);
  }

  // ============================================================================
  // Private
  // ============================================================================

  private _render(): void {
    ensureStyles();

    const hasLabel = this.hasAttribute('label-default-text') || this.hasAttribute('label-dict-key');

    this.innerHTML = /* html */ `
      <span class="as-form-field__label"${hasLabel ? ' style="display:flex"' : ''}>
        <as-text class="label-text">${this.getAttribute('label-default-text') ?? ''}</as-text>
      </span>
      <input class="as-form-field__input" />
    `;

    this._labelRowEl = this.querySelector('.as-form-field__label');
    this._labelTextEl = this.querySelector('.label-text');
    this._inputEl = this.querySelector('input');
  }

  private _handleInput(): void {
    // User typing: the input owns its value. We only mirror it to the form +
    // validity. We never write back to input.value here, so the caret is safe.
    this._value = this._inputEl?.value ?? '';
    this._syncFormValue();
    this._syncValidity();
  }

  private _handleChange(): void {
    this._value = this._inputEl?.value ?? '';
    this._syncFormValue();
    this._syncValidity();
  }

  private _reflectPassthrough(name: string, value: string | null): void {
    if (!this._inputEl) return;
    if (value === null) {
      this._inputEl.removeAttribute(name);
    } else {
      this._inputEl.setAttribute(name, value);
    }
  }

  /** Set the inner input's value without disturbing the caret when unchanged. */
  private _applyValue(value: string): void {
    this._value = value;
    if (this._inputEl && this._inputEl.value !== value) {
      this._inputEl.value = value;
    }
  }

  private _applyBooleanState(): void {
    if (!this._inputEl) return;
    this._inputEl.required = this.hasAttribute('required');
    this._inputEl.disabled = this.hasAttribute('disabled');
    this._inputEl.readOnly = this.hasAttribute('readonly');
  }

  private _syncFormValue(): void {
    this._internals.setFormValue(this.value);
  }

  private _syncValidity(): void {
    if (!this._inputEl) return;
    if (this._inputEl.validity.valid) {
      this._internals.setValidity({});
    } else {
      this._internals.setValidity(
        validityToFlags(this._inputEl.validity),
        this._inputEl.validationMessage,
        this._inputEl
      );
    }
  }

  private _updateLabel(): void {
    if (!this._labelTextEl || !this._labelRowEl) return;
    const dictKey = this.getAttribute('label-dict-key');
    const defaultText = this.getAttribute('label-default-text') ?? '';

    if (dictKey) {
      this._labelTextEl.setAttribute('dict-key', dictKey);
    } else {
      this._labelTextEl.removeAttribute('dict-key');
    }
    this._labelTextEl.textContent = defaultText;

    const hasLabel = !!defaultText || !!dictKey;
    this._labelRowEl.style.display = hasLabel ? 'flex' : 'none';
  }
}

customElements.define('as-form-field', AsFormField);
